import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { firstValueFrom } from 'rxjs';
import { JiraService } from './jira.service';

export interface FeedbackPayload {
  type: 'bug' | 'feature' | 'improvement';
  title: string;
  description: string;
  screenshot?: string | null;
  consoleLogs?: Array<{
    type: string;
    data: string;
    timestamp: number;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {
  constructor(
    private http: HttpClient,
    private auth: AngularFireAuth,
    private jiraService: JiraService
  ) {}

  async submitFeedback(feedback: FeedbackPayload): Promise<any> {
    try {
      // Create the description with optional attachments
      let description = feedback.description + '\n\n';
      
      // Add console logs if available
      if (feedback.consoleLogs && feedback.consoleLogs.length > 0) {
        description += 'h3. Console Logs\n';
        description += '{noformat}\n';
        
        // Convert console logs to string format
        feedback.consoleLogs.forEach(log => {
          const date = new Date(log.timestamp);
          description += `[${date.toLocaleString()}] [${log.type.toUpperCase()}] ${log.data}\n`;
        });
        
        description += '{noformat}\n\n';
      }

      // Get user info from Firebase
      const user = await firstValueFrom(this.auth.user);
      description += `\nSubmitted by: ${user?.email || 'Unknown user'}`;

      const issueType = this.mapFeedbackTypeToJiraIssueType(feedback.type);

      const jiraPayload = {
        fields: {
          project: {
            key: environment.jiraProjectKey
          },
          summary: feedback.title,
          description: description,
          issuetype: {
            name: issueType
          }
        }
      };

      // Create the issue with all content in one call
      const result = await this.jiraService.createIssue({
        ...jiraPayload,
        screenshot: feedback.screenshot
      });

      return result;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }

  private mapFeedbackTypeToJiraIssueType(type: string): string {
    switch (type) {
      case 'bug':
        return 'Bug';
      case 'feature':
        return 'Story';
      case 'improvement':
        return 'Improvement';
      default:
        return 'Task';
    }
  }
} 